import { IMAGES } from "@shared/constants";
import { Link, useLocation } from "wouter";
import { motion } from "framer-motion";
import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { getFreshDirectoryUrl } from "@/lib/freshRouteNavigation";
import { trpc } from "@/lib/trpc";
import CannabisLeafIcon from "@/components/CannabisLeafIcon";
import { LEAF_ICON } from "@/assets/leafIcon";
import {
  MapPin, ShoppingCart, Users, BookOpen, Bot,
  Star, ArrowRight, Shield, Zap, Globe, Crown,
  TrendingUp, Heart, Search, Sparkles, Store, Gift,
  CheckCircle, Lock, Flame, Target, BadgeCheck
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" as const }
  }),
};

function hideBrokenHomepageImage(event: React.SyntheticEvent<HTMLImageElement>) {
  event.currentTarget.style.display = "none";
}

const features = [
  {
    icon: MapPin, title: "Dispensary Finder",
    desc: "Explore dispensary profiles near you. View menus, deals, reviews, photos, contact details, and clearly labeled sponsored placements when each business provides them.",
    href: "/dispensaries", color: "text-primary",
    bg: "bg-primary/10", image: IMAGES.dispensaryHero,
    cta: "Explore Dispensaries"
  },
  {
    icon: null, title: "Strain Intelligence",
    desc: "Stop guessing what works for you. Get detailed strain profiles with real effects, terpene data, and THC/CBD levels — matched to what you actually need.",
    href: "/strains", color: "text-emerald-light",
    bg: "bg-emerald-light/10", image: IMAGES.strainHero,
    cta: "Find Your Perfect Strain"
  },
  {
    icon: ShoppingCart, title: "Order & Save",
    desc: "When a participating business publishes products, compare available menu items and request eligible pickup or delivery — with no middleman order commission.",
    href: "/shop", color: "text-gold",
    bg: "bg-gold/10", image: IMAGES.orderSaveHero,
    cta: "Get Exclusive Drops"
  },
  {
    icon: Users, title: "Cannabis Community",
    desc: "Connect with real enthusiasts, not bots. Share experiences, join local groups, get recommendations from people who actually know their stuff.",
    href: "/community", color: "text-chart-3",
    bg: "bg-chart-3/10", image: IMAGES.communityHero,
    cta: "Join the Community"
  },
  {
    icon: BookOpen, title: "Cannabis Education",
    desc: "Stay informed with legalization updates, medical research, dosing guides, and expert content — everything you need to consume smarter.",
    href: "/learn", color: "text-chart-4",
    bg: "bg-chart-4/10", image: IMAGES.educationHero,
    cta: "Learn Before You Burn"
  },
  {
    icon: Bot, title: "Budtender",
    desc: "Tell us your mood, your tolerance, and what you're looking for. We'll recommend the exact strain and product — like having a budtender in your pocket.",
    href: "/budtender", color: "text-accent",
    bg: "bg-accent/10", image: IMAGES.budtenderHero,
    cta: "Get Your Recommendation"
  },
];

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M+`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K+`;
  return `${n.toLocaleString()}+`;
}

const trustSignals = [
  { icon: BadgeCheck, text: "Source-aware dispensary data" },
  { icon: Shield, text: "No fake reviews · Sponsored placements clearly labeled" },
  { icon: Lock, text: "Your data stays private, always" },
  { icon: Flame, text: "Menus & pricing when published" },
];

function CannabisTypeSlideshow() {
  const [, setLocation] = useLocation();
  const slides = [
    {
      customImg: "https://d2xsxph8kpxj0f.cloudfront.net/310519663327876234/RBPh534jAfSh7iDqSuSqBr/icon-medical-MuxbyKa3EYB7EUQKAkPkLC.webp",
      label: "Medical",
      href: "/dispensaries?type=medical",
      gradientBg: "from-blue-950 via-blue-900/80 to-slate-900",
      glowOrb: "bg-blue-500",
      iconBg: "bg-blue-500/20",
      iconBorder: "border-blue-400/30",
      titleColor: "text-blue-200",
      ctaColor: "text-blue-300",
      border: "border-blue-500/25",
      tagStyle: "bg-blue-500/15 text-blue-200 border-blue-400/25 hover:bg-blue-500/25",
      desc: "Find licensed medical dispensaries, patient-focused strains, and products designed for relief. Filter by condition, effect, and THC/CBD ratio.",
      tags: [
        { label: "Pain Relief", href: "/strains?effect=pain" },
        { label: "Anxiety", href: "/strains?effect=anxiety" },
        { label: "Sleep", href: "/strains?effect=sleep" },
        { label: "PTSD", href: "/strains?effect=ptsd" },
        { label: "Inflammation", href: "/strains?effect=inflammation" },
      ],
    },
    {
      customImg: "/manus-storage/icon-recreational-nug_9a7fe380.png",
      label: "Recreational",
      href: "/dispensaries?type=recreational",
      gradientBg: "from-emerald-950 via-emerald-900/70 to-slate-900",
      glowOrb: "bg-emerald-500",
      iconBg: "bg-emerald-500/20",
      iconBorder: "border-emerald-400/30",
      titleColor: "text-emerald-200",
      ctaColor: "text-emerald-300",
      border: "border-emerald-500/25",
      tagStyle: "bg-emerald-500/15 text-emerald-200 border-emerald-400/25 hover:bg-emerald-500/25",
      desc: "Discover top-shelf flower, edibles, concentrates, and more from recreational dispensaries near you. Real menus, real deals, no fluff.",
      tags: [
        { label: "Flower", href: "/shop?category=flower" },
        { label: "Edibles", href: "/shop?category=edible" },
        { label: "Vapes", href: "/shop?category=vape" },
        { label: "Concentrates", href: "/shop?category=concentrate" },
        { label: "Pre-rolls", href: "/shop?category=preroll" },
      ],
    },
    {
      customImg: null as string | null,
      label: "Hemp & CBD",
      href: "/dispensaries?type=hemp",
      gradientBg: "from-lime-950 via-lime-900/70 to-slate-900",
      glowOrb: "bg-lime-500",
      iconBg: "bg-lime-500/20",
      iconBorder: "border-lime-400/30",
      titleColor: "text-lime-200",
      ctaColor: "text-lime-300",
      border: "border-lime-500/25",
      tagStyle: "bg-lime-500/15 text-lime-200 border-lime-400/25 hover:bg-lime-500/25",
      desc: "Shop federally legal hemp-derived CBD, Delta-8, and wellness products available nationwide — no dispensary required.",
      tags: [
        { label: "CBD", href: "/shop?category=cbd" },
        { label: "Delta-8", href: "/shop?category=delta8" },
        { label: "THC-A", href: "/shop?category=thca" },
        { label: "Wellness", href: "/strains?effect=wellness" },
        { label: "Nationwide", href: "/dispensaries?type=hemp" },
      ],
    },
  ];

  const [active, setActive] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive(prev => (prev + 1) % slides.length);
    }, 4000);
  };

  useEffect(() => {
    startTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const slide = slides[active];

  return (
    <div className="relative">
      {/* Slide */}
      <motion.div
          key={active}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -60 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className={`relative overflow-hidden rounded-2xl border ${slide.border} cursor-pointer group`}
          style={{ minHeight: '300px' }}
          role="link"
          tabIndex={0}
          aria-label={`Explore ${slide.label}`}
          onClick={() => setLocation(slide.href)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setLocation(slide.href);
            }
          }}
        >
          <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradientBg}`} />
          <div className={`absolute -top-12 -right-12 w-52 h-52 rounded-full ${slide.glowOrb} blur-3xl opacity-30 group-hover:opacity-50 transition-opacity duration-500`} />
          <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="relative z-10 p-7 flex flex-col" style={{ minHeight: '300px' }}>
            <div className={`w-16 h-16 rounded-2xl ${slide.iconBg} backdrop-blur-sm flex items-center justify-center mb-5 shadow-lg border ${slide.iconBorder} ring-1 ring-white/5 overflow-hidden`}>
              {slide.customImg
                ? <img src={slide.customImg} alt={slide.label} className="w-full h-full object-cover" />
                : <CannabisLeafIcon className="w-12 h-12" />}
            </div>
            <h3 className={`text-2xl font-black mb-2 tracking-tight ${slide.titleColor}`}>{slide.label}</h3>
            <p className="text-sm text-white/60 leading-relaxed mb-5 flex-1">{slide.desc}</p>
            <div className="flex flex-wrap gap-2" onClick={e => e.preventDefault()}>
              {slide.tags.map(tag => (
                <Link key={tag.label} href={tag.href}
                  onClick={e => e.stopPropagation()}
                  className={`text-xs px-3 py-1.5 rounded-full font-semibold backdrop-blur-sm border transition-all hover:scale-105 ${slide.tagStyle}`}
                >{tag.label}</Link>
              ))}
            </div>
            <div className={`mt-5 flex items-center gap-1.5 text-sm font-semibold ${slide.ctaColor} group-hover:gap-3 transition-all duration-300`}>
              <span>Explore {slide.label}</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
      </motion.div>
      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mt-4">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => { setActive(i); startTimer(); }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${i === active ? 'bg-primary w-6' : 'bg-white/30'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const homepageQueryOptions = {
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  } as const;
  const { data: stats } = trpc.platformStats.counts.useQuery(undefined, homepageQueryOptions);
  const { data: heroBannerData } = trpc.dispensaries.heroBanner.useQuery(undefined, homepageQueryOptions);
  const { data: liveAdPlacements } = trpc.advertising.publicPlacements.useQuery(undefined, homepageQueryOptions);
  const homepageTakeover = useMemo(() => (liveAdPlacements || []).find((placement: any) => placement.tier === "homepage_takeover") || null, [liveAdPlacements]);

  // Geolocation for smoke buddy section
  const [geoCoords, setGeoCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [manualCityInput, setManualCityInput] = useState("");
  const [submittedCityQuery, setSubmittedCityQuery] = useState("");
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeoCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setLocationDenied(true),
        { timeout: 6000 }
      );
    } else {
      setLocationDenied(true);
    }
  }, []);
  const { data: nearbyData } = trpc.matching.nearbyMatches.useQuery(
    geoCoords
      ? { lat: geoCoords.lat, lng: geoCoords.lng, limit: 3 }
      : submittedCityQuery
      ? { cityQuery: submittedCityQuery, limit: 3 }
      : { limit: 3 },
    { staleTime: 30_000, enabled: !!(geoCoords || submittedCityQuery) }
  );

  const statItems = [
    { value: stats ? `${formatCount(stats.strains)}` : "10,000+", label: "Strains Profiled", icon: null },
    { value: stats ? `${formatCount(stats.dispensaries)}` : "5,000+", label: "Verified Dispensaries", icon: BadgeCheck },
    { value: stats ? String(stats.states) : "50", label: "Markets Tracked", icon: Globe },
  ];

  return (
    <div className="homepage-shell min-w-0 overflow-x-clip" style={{ overflowAnchor: "none" }}>
      {/* Hero Section */}
      <section
        className="relative min-h-[70svh] md:min-h-[90vh] flex items-end md:items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img src={IMAGES.heroBg} alt="" className="w-full h-full object-cover opacity-100" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/70" />
        </div>
        {/* Floating VIP marquee text — no background, just white letters */}
          <div className="homepage-vip-ticker absolute -top-1 left-0 right-0 z-[5] pointer-events-none py-1 md:top-0 md:py-2">
          <Link href="/events">
            <div className="pointer-events-auto cursor-pointer">
                <div className="vip-marquee-wrapper flex">
                <div className="vip-marquee-half flex items-center flex-shrink-0">
                  {[...Array(2)].map((_, i) => (
                    <span key={i} className="inline-flex items-center whitespace-nowrap">
                      <img src={LEAF_ICON} alt="" aria-hidden="true" className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 object-contain flex-shrink-0 mr-2" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
                      <span className="text-white font-semibold tracking-[0.2em] uppercase text-[11px] sm:text-[13px] md:text-[15px]">Find Your Smoke Buddy • Find The Best Weed • Releafa Lounge Live Chat</span>
                      <span className="text-white/40 mx-8">✦</span>
                    </span>
                  ))}
                </div>
                <div className="vip-marquee-half flex items-center flex-shrink-0">
                  {[...Array(2)].map((_, i) => (
                    <span key={i} className="inline-flex items-center whitespace-nowrap">
                      <img src={LEAF_ICON} alt="" aria-hidden="true" className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 object-contain flex-shrink-0 mr-2" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
                      <span className="text-white font-semibold tracking-[0.2em] uppercase text-[11px] sm:text-[13px] md:text-[15px]">Find Your Smoke Buddy • Find The Best Weed • Releafa Lounge Live Chat</span>
                      <span className="text-white/40 mx-8">✦</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Link>
        </div>

        {homepageTakeover && (
          <div className="absolute left-4 right-4 top-5 z-20 md:left-8 md:right-8 md:top-14">
            <a href={`/dispensary/${homepageTakeover.slug}`} className="relative block h-[58px] overflow-hidden rounded-xl border bg-gradient-to-br from-[#11251f] via-[#0b1713] to-[#06110d] transition-transform hover:scale-[1.01] md:h-[64px]" style={{ borderColor: `${homepageTakeover.brandColor || "#f59e0b"}cc` }}>
              {homepageTakeover.coverUrl && <img src={homepageTakeover.coverUrl} alt="" onError={hideBrokenHomepageImage} className="absolute inset-0 h-full w-full object-cover" decoding="async" />}
              <span className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/25" />
              <span className="relative flex h-full items-center gap-2 px-2.5 md:px-3">
                {homepageTakeover.logoUrl && <img src={homepageTakeover.logoUrl} alt="" onError={hideBrokenHomepageImage} className="h-8 w-8 shrink-0 rounded-lg border border-white/30 bg-black/70 object-contain p-0.5 shadow-lg md:h-10 md:w-10" />}
                <span className="min-w-0">
                  <span className="neon-pulse block text-[8px] font-bold uppercase tracking-[0.16em] text-white">Exclusive Homepage Takeover</span>
                  <span className="block truncate text-xs font-bold text-white md:text-sm">{homepageTakeover.name}</span>
                </span>
              </span>
            </a>
          </div>
        )}

        <div className="container relative z-10 pt-20 pb-10 md:pt-24 md:pb-16">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card text-sm text-primary mb-6"
            >
              <Sparkles className="w-4 h-4" />
              <span>The Cannabis Releafa Network</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6"
            >
              <span className="text-foreground">Find. </span>
              <span className="text-gradient-emerald">Order. </span>
              <span className="text-gradient-gold">Elevate.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl"
            >
              Discover cannabis businesses from <span className="text-primary font-medium">public directory sources and business updates</span>,
              alongside community reviews and recommendations shaped around what matters to you.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-emerald-light font-semibold gap-2 glow-emerald text-base px-6"
                onClick={() => window.location.assign(getFreshDirectoryUrl())}
              >
                <Target className="w-5 h-5" />
                Explore Dispensaries Near Me
              </Button>
              <Link href="/shop">
                <Button size="lg" variant="outline" className="border-gold/30 text-gold hover:bg-gold/10 font-semibold gap-2 text-base px-6">
                  <Gift className="w-5 h-5" />
                  Get Exclusive Drops
                </Button>
              </Link>
            </motion.div>

            {/* Trust signals under hero CTAs */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-6 grid grid-cols-2 gap-x-3 gap-y-2 sm:gap-x-5 md:grid-cols-4"
            >
              {trustSignals.map((t) => (
                <div key={t.text} className="flex min-w-0 items-center gap-1.5 text-[11px] leading-tight text-muted-foreground sm:text-xs">
                  <t.icon className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                  <span className="min-w-0">{t.text}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Floating particles effect */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-primary/30"
              style={{ left: `${15 + i * 15}%`, top: `${20 + (i % 3) * 25}%` }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.2, 0.6, 0.2],
              }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.3,
              }}
            />
          ))}
        </div>
      </section>

      <section className="relative z-10 mb-6 h-[104px]">
        {homepageTakeover ? (
          <div className="container">
            <motion.a
              href={`/dispensary/${homepageTakeover.slug}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="flex h-[104px] items-center gap-4 overflow-hidden rounded-2xl border bg-black/65 p-4 backdrop-blur-md transition-transform hover:-translate-y-0.5"
              style={{ borderColor: `${homepageTakeover.brandColor || "#f59e0b"}aa` }}
            >
              {homepageTakeover.logoUrl && <img src={homepageTakeover.logoUrl} alt="" className="h-14 w-14 rounded-xl border border-white/20 bg-black/40 object-contain p-1.5" />}
              <span className="min-w-0 flex-1">
                <span className="neon-pulse block mb-1 text-[10px] font-bold uppercase tracking-widest text-white">Now taking over Releafa</span>
                <span className="block truncate text-lg font-bold text-white">{homepageTakeover.name}</span>
                <span className="block text-xs text-white/70">{homepageTakeover.city}, {homepageTakeover.state} · Explore the featured menu</span>
              </span>
              <span className="hidden rounded-lg px-3 py-2 text-xs font-bold text-black sm:inline" style={{ backgroundColor: homepageTakeover.brandColor || "#f59e0b" }}>Explore</span>
            </motion.a>
          </div>
        ) : (
          <div className="container h-[104px]" aria-hidden="true" />
        )}
      </section>

      {/* Stats Bar */}
      <section className="relative z-10 mt-2 mb-4 h-32 md:h-44">
        {heroBannerData ? (
          <div className="container">
            <a href={`/dispensary/${(heroBannerData as any).slug}`}
              className="group relative block h-32 overflow-hidden rounded-2xl border-2 shadow-2xl md:h-44"
              style={{ borderColor: (heroBannerData as any).brandColor || '#7c3aed' }}>
              <div className="relative h-32 md:h-44 overflow-hidden bg-gradient-to-br from-[#11251f] via-[#0b1713] to-[#06110d]">
                {(heroBannerData as any).coverUrl && <img src={(heroBannerData as any).coverUrl} alt={(heroBannerData as any).name}
                  onError={hideBrokenHomepageImage} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" decoding="async" />}
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
                <div className="absolute inset-0 flex items-center px-6 md:px-10">
                  <div className="flex items-center gap-4">
                    {(heroBannerData as any).logoUrl && (
                      <div className="w-14 h-14 md:w-20 md:h-20 rounded-xl border-2 border-white/30 bg-black/50 overflow-hidden flex-shrink-0">
                        <img src={(heroBannerData as any).logoUrl} alt="" onError={hideBrokenHomepageImage} className="w-full h-full object-contain p-1" />
                      </div>
                    )}
                    <div>
                      <span className="neon-pulse text-[10px] font-bold text-white uppercase tracking-widest block mb-1">SPONSORED</span>
                      <h3 className="text-xl md:text-3xl font-bold text-white line-clamp-1">{(heroBannerData as any).name}</h3>
                      <p className="text-sm text-white/70 mt-0.5">{(heroBannerData as any).city}, {(heroBannerData as any).state} · Premium Cannabis Dispensary</p>
                    </div>
                  </div>
                  <div className="ml-auto flex-shrink-0">
                    <span className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white"
                      style={{ backgroundColor: (heroBannerData as any).brandColor || '#7c3aed' }}>
                      View Menu →
                    </span>
                  </div>
                </div>
              </div>
            </a>
          </div>
        ) : (
          <div className="container h-32 md:h-44" aria-hidden="true" />
        )}
      </section>
      <section className="relative mt-4 z-20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-6 grid grid-cols-2 md:grid-cols-3 gap-6"
          >
            {statItems.map(({ value, label, icon: Icon }) => (
              <div key={label} className="text-center last:col-span-2 md:last:col-span-1">
                <div className="flex items-center justify-center gap-2 mb-1">
                  {Icon && <Icon className="w-5 h-5 text-primary" />}
                  <span className="text-2xl md:text-3xl font-bold text-gradient-main">{value}</span>
                </div>
                <span className="text-sm text-muted-foreground">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Find Your Smoke Buddy — Hero Feature Section */}
      <section className="py-12 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-primary/8 rounded-full blur-3xl" />
        </div>
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border border-primary/40 overflow-hidden"
            style={{ background: 'linear-gradient(135deg, oklch(0.17 0.03 160) 0%, oklch(0.14 0.02 160) 60%, oklch(0.16 0.025 160) 100%)' }}
          >
            <div className="flex flex-col items-center text-center p-8 md:p-12">
              {/* RELEAFA EXCLUSIVE badge */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/50 bg-primary/10 text-primary text-xs font-black uppercase tracking-widest mb-6">
                Releafa Exclusive
              </div>

              {/* Headline */}
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-2">
                Find Your
              </h2>
              <h2
                className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-5"
                style={{
                  background: 'linear-gradient(90deg, oklch(0.75 0.22 160), oklch(0.85 0.20 155), oklch(0.75 0.22 160))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  filter: 'drop-shadow(0 0 18px oklch(0.72 0.19 160 / 0.55))',
                }}
              >
                Smoke Buddy
              </h2>

              {/* Description + ONLY ON RELEAFA badge */}
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-2 max-w-lg">
                Match with real cannabis enthusiasts near you. Same vibe, same strains, same energy.
              </p>
              <span className="inline-block px-3 py-1 rounded-md bg-primary/20 border border-primary/40 text-primary text-xs font-black uppercase tracking-widest mb-8">
                Only on Releafa
              </span>

              {/* Feature bullets */}
              <ul className="space-y-2.5 mb-8 text-left w-full max-w-sm mx-auto">
                {[
                  "Match by strain preference & vibe",
                  "Find smoke buddies near you or worldwide",
                  "Link up for a session — wherever you are",
                  "Unlock Direct Messaging with your matches",
                ].map(f => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-foreground/80">
                    <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-3 h-3 text-primary" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              {/* CTA with glow spotlight */}
              <div className="relative mb-10 w-full max-w-sm mx-auto">
                <div className="absolute inset-0 rounded-2xl blur-2xl opacity-60" style={{ background: 'radial-gradient(ellipse at center, oklch(0.72 0.19 160 / 0.5) 0%, transparent 70%)' }} />
                <Link href="/match" className="relative block">
                  <Button size="lg" className="w-full bg-primary text-primary-foreground hover:bg-emerald-light font-black gap-2 text-base py-4 rounded-xl" style={{ boxShadow: '0 0 24px oklch(0.72 0.19 160 / 0.5), 0 0 48px oklch(0.72 0.19 160 / 0.2)' }}>
                    <Heart className="w-5 h-5" />
                    Find My Smoke Buddy
                  </Button>
                </Link>
              </div>

              {/* LIVE MATCHES label — dynamic based on visitor location */}
              {(() => {
                const cardColors = [
                  { glow: "0 0 14px oklch(0.72 0.19 160 / 0.55)", border: "border-primary/50", bg: "bg-primary/8" },
                  { glow: "0 0 10px oklch(0.6 0.2 300 / 0.3)", border: "border-purple-500/35", bg: "bg-purple-500/5" },
                  { glow: "0 0 8px oklch(0.6 0.2 240 / 0.25)", border: "border-blue-500/30", bg: "bg-blue-500/5" },
                ];
                const fallbackPhotos = [
                  "/manus-storage/buddy-profile-1_9b588a7e.png",
                  "/manus-storage/buddy-profile-2_00c6c010.png",
                  "/manus-storage/buddy-profile-3_4d85f954.png",
                ];
                const hasRealData = nearbyData && (nearbyData as any).matches && (nearbyData as any).matches.length > 0;
                const displayCity = (nearbyData as any)?.city || null;
                const activeCount = (nearbyData as any)?.activeCount ?? 0;
                const cards = hasRealData
                  ? nearbyData.matches.map((m: any, i: number) => ({
                      name: m.name,
                      city: m.locationCity || "Nearby",
                      strain: m.favoriteStrain || "Blue Dream",
                      photo: m.avatarUrl || fallbackPhotos[i % 3],
                      distance: m.distanceMi ? `${m.distanceMi} mi` : null,
                      matchPct: m.matchPct,
                    }))
                  : [
                      { name: "Kush Man", city: "Houston, TX", strain: "Blue Dream", photo: fallbackPhotos[0], distance: "1.2 mi", matchPct: 97 },
                      { name: "Cleo", city: "Atlanta, GA", strain: "Gelato", photo: fallbackPhotos[1], distance: "3.8 mi", matchPct: 94 },
                      { name: "DC Sniper", city: "Los Angeles, CA", strain: "OG Kush", photo: fallbackPhotos[2], distance: "0.7 mi", matchPct: 91 },
                    ];
                return (
                  <>
                    <div className="flex min-h-[42px] flex-col items-center gap-1 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
                        </span>
                        <span className="text-xs font-black uppercase tracking-widest text-primary">
                          {(nearbyData as any)?.isWorldwide ? "Worldwide Matches" : "Live Matches Near You"}
                        </span>
                      </div>
                      {hasRealData && activeCount > 0 && displayCity ? (
                        <p className="text-xs text-muted-foreground">{activeCount} {activeCount === 1 ? "member" : "members"} active near {displayCity}</p>
                      ) : hasRealData && activeCount > 0 ? (
                        <p className="text-xs text-muted-foreground">{activeCount} {activeCount === 1 ? "member" : "members"} active near you</p>
                      ) : (hasRealData && (nearbyData as any)?.isWorldwide) ? (
                        <p className="text-xs text-muted-foreground">Showing members from across the Releafa network</p>
                      ) : null}
                      {/* ZIP fallback — only when location is denied and no city resolved */}
                      {false && (
                        <div className="flex gap-1.5 mt-2 w-full max-w-xs">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={manualCityInput}
                            onChange={e => setManualCityInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && manualCityInput.trim()) setSubmittedCityQuery(manualCityInput.trim()); }}
                            placeholder="Enter city or ZIP..."
                            className="flex-1 text-xs px-3 py-2 rounded-lg border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                          />
                          <button
                            onClick={() => { if (manualCityInput.trim()) setSubmittedCityQuery(manualCityInput.trim()); }}
                            className="text-xs px-3 py-2 rounded-lg bg-primary text-primary-foreground font-bold whitespace-nowrap"
                          >Find</button>
                        </div>
                      )}
                    </div>
                    {/* Profile cards */}
                    <div className="flex flex-col gap-3 w-full max-w-sm mx-auto" style={{ minHeight: "300px", contain: "layout" }}>
                      {cards.map((p: any, i: number) => {
                        const cc = cardColors[i % cardColors.length];
                        const matchColor = p.matchPct >= 96
                          ? { badge: "bg-primary text-primary-foreground", shadow: "0 0 10px oklch(0.72 0.19 160 / 0.7)" }
                          : p.matchPct >= 93
                          ? { badge: "bg-primary/60 text-primary-foreground", shadow: "0 0 6px oklch(0.72 0.19 160 / 0.4)" }
                          : { badge: "bg-primary/30 text-primary/80", shadow: "none" };
                        return (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 15 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.12, duration: 0.45 }}
                            className={`flex items-center gap-3 rounded-2xl border p-3.5 backdrop-blur-md ${cc.border} ${cc.bg}`}
                            style={{ boxShadow: cc.glow }}
                          >
                            <div className="relative flex-shrink-0">
                              <img src={p.photo} alt={p.name} className={`w-12 h-12 rounded-full object-cover border-2 ${cc.border}`} style={{ filter: "brightness(0.9) contrast(1.05)" }} />
                              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-primary border-2 border-card" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-bold text-sm text-foreground">{p.name}</div>
                              <div className="text-[11px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                                {p.city}{p.distance ? ` • ${p.distance}` : ""}
                              </div>
                              <div className="flex items-center gap-1 mt-0.5">
                                <img src="/manus-storage/strain-bud-icon_01065f43.png" alt="" className="w-3.5 h-3.5 object-contain" />
                                <span className="truncate text-xs text-primary font-semibold">{p.strain}</span>
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <div className="flex flex-col items-end gap-1">
                                <span className={`text-[11px] font-black px-2.5 py-1 rounded-full ${matchColor.badge}`} style={{ boxShadow: matchColor.shadow }}>
                                  {p.matchPct}% MATCH
                                </span>
                                <span className="text-[10px] text-primary/70 font-semibold">View Match →</span>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}

                    </div>
                  </>
                );
              })()}
            </div>
          </motion.div>
        </div>
      </section>
      {/* Medical / Recreational / Hemp */}
      <section className="py-10">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              One Platform. <span className="text-gradient-main">Every Kind of Cannabis.</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Whether you're a patient, a recreational consumer, or a hemp wellness seeker — Releafa has you covered.
            </p>
          </motion.div>
          <CannabisTypeSlideshow />
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Everything You Need, <span className="text-gradient-emerald">Nothing You Don't</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Sponsored placements are clearly labeled. No fake reviews. Just real data, real dispensaries, and real value.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <Link href={f.href}>
                  <div className="group glass-card overflow-hidden hover:border-primary/30 transition-all duration-300 h-full">
                    <div className="relative h-36 overflow-hidden">
                      <img src={f.image} alt="" className="w-full h-full object-cover opacity-85 group-hover:opacity-95 group-hover:scale-105 transition-all duration-500" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-card/60 to-transparent" />
                      <div className={`absolute top-3 left-3 w-10 h-10 rounded-xl ${f.bg} flex items-center justify-center`}>
                        {f.icon ? <f.icon className={`w-5 h-5 ${f.color}`} /> : <CannabisLeafIcon className="w-5 h-5" />}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">{f.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                      <div className="mt-4 flex items-center gap-1.5 text-sm text-primary font-medium">
                        {f.cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dispensary Owner CTA Banner */}
      <section className="py-12">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card border-gold/20 p-8 md:p-12 flex flex-col md:flex-row items-center gap-8"
          >
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 text-gold text-sm font-medium mb-4">
                <Store className="w-4 h-4" />
                For Dispensary Owners
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Your Customers Are Searching. <span className="text-gradient-gold">Are They Finding You?</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6 max-w-xl">
                Get your dispensary listed on a growing cannabis marketplace. Featured placement, 
                real-time menu management, order routing, analytics dashboard, and direct access to 
                customers actively looking to buy — starting free.
              </p>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Free listing forever
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Verified badge included
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Real-time menu updates
                </div>
                <div className="flex items-center gap-2 text-sm text-gold">
                  <CheckCircle className="w-4 h-4 text-gold" />
                  50% off paid plans for your first year
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  No contracts, cancel anytime
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3 shrink-0">
              <Link href="/dispensary/register">
                <Button size="lg" className="bg-gold text-gold-foreground hover:bg-gold/90 font-bold gap-2 text-base px-8 w-full">
                  <Store className="w-5 h-5" />
                  Claim Your Dispensary
                </Button>
              </Link>
              <Link href="/pricing">
                <Button size="lg" variant="outline" className="border-gold/30 text-gold hover:bg-gold/10 font-semibold gap-2 w-full">
                  See Pricing Plans
                </Button>
              </Link>
              <a href="tel:+18007069680" className="text-sm text-center text-muted-foreground hover:text-gold transition-colors mt-1">
                Or call us: <span className="font-medium text-gold">+1 (800) 706-9680</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why RELEAFA NETWORK */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/3 to-transparent" />
        <div className="container relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Cannabis Pros Choose <span className="text-gradient-gold">RELEAFA</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              We're not another Leafly clone. We built what the cannabis industry actually needs.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: BadgeCheck, title: "Source-Aware Listings", desc: "We show listing source and business information when it is available." },
              { icon: Zap, title: "Recommendations That Actually Help", desc: "Our Budtender uses your chosen preferences to recommend strains that actually match — not ad placement." },
              { icon: TrendingUp, title: "Menus & Prices When Published", desc: "See menu items and prices when a participating business makes them available on Releafa." },
              { icon: Crown, title: "VIP Rewards That Pay", desc: "Earn points on every order, unlock exclusive drops, and get access to the RELEAFA Lounge — our members-only community." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="glass-card p-6 text-center hover:border-gold/20 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-gold p-10 md:p-16 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-gold/5" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Stop Settling for <span className="text-gradient-main">Outdated Platforms</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-8">
                Whether you're looking for your next strain, your nearest dispensary, or your next 
                business opportunity — RELEAFA has you covered.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link href="/dispensaries">
                  <Button size="lg" className="bg-primary text-primary-foreground hover:bg-emerald-light font-bold gap-2 text-base px-6">
                    <Target className="w-5 h-5" />
                    Explore Dispensaries Near Me
                  </Button>
                </Link>
                <Link href="/dispensary/register">
                  <Button size="lg" variant="outline" className="border-gold/30 text-gold hover:bg-gold/10 font-bold gap-2 text-base px-6">
                    <Store className="w-5 h-5" />
                    Claim Your Dispensary
                  </Button>
                </Link>
                <Link href="/shop">
                  <Button size="lg" variant="outline" className="border-primary/30 text-primary hover:bg-primary/10 font-bold gap-2 text-base px-6">
                    <Gift className="w-5 h-5" />
                    Get Exclusive Drops
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-muted-foreground mt-6">
                Need help? Call{" "}
                <a href="tel:+18007069680" className="text-primary hover:underline font-medium">+1 (800) 706-9680</a>
                {" "}&middot; Mon–Fri 9am–5pm CT
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SEO Content Section - keyword-rich visible text for search engine indexing */}
      <section className="py-16 bg-muted/10 border-t border-border/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <h2 className="text-base font-bold mb-3 text-foreground">Cannabis Finder Near You</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Releafa is a <strong className="text-foreground/80">cannabis finder</strong> for discovering local dispensary profiles. Whether you're searching for <strong className="text-foreground/80">weed near me</strong>, a nearby dispensary, or businesses in a new city, Releafa brings location details together with menus, reviews, deals, and contact information when those details are published for a listing.
              </p>
            </div>
            <div>
              <h2 className="text-base font-bold mb-3 text-foreground">Cannabis Marketplace</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                The Releafa <strong className="text-foreground/80">cannabis marketplace</strong> helps participating dispensaries publish flower, edibles, concentrates, vapes, pre-rolls, tinctures, and topicals. Compare the menu information a business has made available and request <strong className="text-foreground/80">delivery</strong> or pickup only where that business offers the service.
              </p>
            </div>
            <div>
              <h2 className="text-base font-bold mb-3 text-foreground">Dispensary Directory & Strain Guide</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Find <strong className="text-foreground/80">dispensaries near me</strong> with Releafa's state-by-state dispensary directory covering every legal cannabis market. Pair it with our strain guide — 1,000+ indica, sativa, and hybrid strains with THC/CBD levels, effects, and terpene profiles — to find exactly what you need.
              </p>
            </div>
          </div>
          <div className="mt-10 pt-8 border-t border-border/20 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { title: "Find Weed Near Me", desc: "Search dispensaries by city, state, or zip code with live inventory.", href: "/dispensaries" },
              { title: "Cannabis Delivery", desc: "Order cannabis for same-day delivery from dispensaries near you.", href: "/dispensaries" },
              { title: "Marijuana Dispensary Finder", desc: "Browse licensed marijuana dispensaries with menus, reviews, and hours.", href: "/dispensaries" },
              { title: "Online Cannabis Shop", desc: "Shop cannabis products online and pick up in-store or get delivery.", href: "/shop" },
            ].map((item) => (
              <a key={item.title} href={item.href} className="block group">
                <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors mb-1">{item.title}</h3>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
